/**
 * CalibrationLab — redesigned Phase 2.C v2
 *
 * The original heatmap-led design was visually noisy and the data was
 * sparse (most cells empty). Redesign leads with the operator's daily
 * question: "where am I miscalibrated, and how badly?"
 *
 * Layout:
 *   1. Health hero — pill (green/yellow/red), totals, biggest miss
 *      surfaced prominently as a callout
 *   2. Bucket cards — one per active confidence bucket, large hit
 *      rate number, target marker on a horizontal scale, n + delta
 *   3. Sport grid — per-sport tiles with WR / P&L / per-bucket dots
 *   4. Single ECharts horizontal bar chart comparing actual vs target
 *      across all buckets (the only chart — focused, legible)
 */
import { html, type TemplateResult } from "lit-html";
import * as echarts from "echarts";
import { store } from "../data/store";
import type {
  CalibrationSnapshot, CalibrationCell, CalibrationSport,
  CalibrationHistory,
} from "../data/types";
import { renderExplainButton } from "./ExplainButton";

interface ChartHandle { inst: echarts.ECharts | null; el: HTMLElement | null; }
const charts = {
  comparison: { inst: null, el: null } as ChartHandle,
  history:    { inst: null, el: null } as ChartHandle,
};
let lastDataKey = "";
let lastHistoryKey = "";
let historyBucket: "90+" | "70-89" | "50-69" | "<50" = "90+";

const SPORT_COLORS: Record<string, string> = {
  all:    "var(--text-secondary)",
  NBA:    "var(--accent-orange-light)",
  WNBA:   "#2dd4bf",
  MLB:    "var(--accent-blue-light)",
  NHL:    "var(--accent-purple-light)",
  NFL:    "#d4a017",
  Soccer: "var(--accent-green-light)",
  UFC:    "var(--accent-red-light)",
  Tennis: "var(--accent-amber)",
};

const BUCKET_TARGETS: Record<string, { lo: number; hi: number; mid: number }> = {
  "90+":   { lo: 90, hi: 100, mid: 95 },
  "70-89": { lo: 70, hi: 89,  mid: 79.5 },
  "50-69": { lo: 50, hi: 69,  mid: 59.5 },
  "<50":   { lo: 0,  hi: 49,  mid: 25 },
};

export function renderCalibrationLab(_onChange: () => void): TemplateResult {
  const e = store.get<CalibrationSnapshot>("calibration");
  if (e.error) return errorView(e.error);
  if (!e.data && e.loading) return skeletonView();
  if (!e.data) return emptyView();

  const cal = e.data;
  const dataKey = `${cal.as_of}/${cal.heatmap.length}/${cal.sports.length}`;
  queueMicrotask(() => mountCharts(cal, dataKey));

  // Find biggest miss across the "all" cohort + per-sport buckets
  const biggestMiss = findBiggestMiss(cal);

  return html`
    <div class="cal-lab">
      ${heroSection(cal, biggestMiss)}
      ${bucketsSection(cal)}
      ${comparisonChartCard()}
      ${historyChartCard(_onChange)}
      ${sportsSection(cal)}
    </div>
  `;
}

// =============================================================
// History chart — daily snapshots of per-sport hit_rate
// =============================================================
function historyChartCard(onChange: () => void): TemplateResult {
  const e = store.get<CalibrationHistory>("calibrationHistory");
  const buckets: Array<typeof historyBucket> = ["90+", "70-89", "50-69", "<50"];

  // Mount the chart on every render — the mounter handles the
  // detached-DOM case via element-identity check.
  queueMicrotask(() => mountHistoryChart(e.data ?? null));

  const empty = !e.data || e.data.snapshots.length === 0;
  return html`
    <section class="panel cal-chart-card">
      <header class="cal-chart-card__head">
        <div>
          <h3>Hit rate over time · ${historyBucket} bucket</h3>
          <span class="cal-chart-card__sub">
            ${e.data?.snapshots.length ?? 0} daily snapshots ·
            target mid = ${BUCKET_TARGETS[historyBucket]?.mid}%
            ${e.data?.note ? ` · ${e.data.note}` : ""}
          </span>
        </div>
        <div class="cal-history__bucket-toggle">
          ${buckets.map((b) => html`
            <button class="cal-history__bucket-btn
                          ${historyBucket === b ? "is-active" : ""}"
                    @click=${() => { historyBucket = b; lastHistoryKey = ""; onChange(); }}>
              ${b}
            </button>
          `)}
        </div>
      </header>
      <div id="cal-history" class="cal-chart-card__canvas"
           style="height:280px;"></div>
      ${empty ? html`
        <div class="cal-history__empty">
          No snapshots yet — first will land at the next 8am ET cron tick.
          Trigger manually with <code>?force=1</code> via the Cmd-K query.
        </div>
      ` : ""}
    </section>
  `;
}

function mountHistoryChart(hist: CalibrationHistory | null): void {
  const newEl = document.getElementById("cal-history");
  if (!newEl) return;

  const stale = charts.history.el && charts.history.el !== newEl;
  if (stale && charts.history.inst) {
    charts.history.inst.dispose();
    charts.history.inst = null;
    lastHistoryKey = "";
  }
  charts.history.el = newEl;
  if (!charts.history.inst) {
    charts.history.inst = echarts.init(newEl, "dark");
  }

  const dataKey = `${historyBucket}/${hist?.snapshots.length ?? 0}/${hist?.snapshots[hist.snapshots.length - 1]?.date ?? ""}`;
  if (dataKey === lastHistoryKey) return;
  lastHistoryKey = dataKey;

  charts.history.inst.setOption(historyOption(hist), true);
}

function historyOption(hist: CalibrationHistory | null): echarts.EChartsOption {
  const snaps = hist?.snapshots ?? [];
  const sports = ["all", "NBA", "WNBA", "MLB", "NHL", "NFL", "Soccer", "UFC"];
  const series = sports.map((sport) => ({
    name: sport,
    type: "line" as const,
    showSymbol: false,
    smooth: 0.3,
    lineStyle: { width: sport === "all" ? 2.5 : 1.5 },
    data: snaps.map((s) => {
      const v = s.per_sport[sport]?.buckets[historyBucket]?.hit_rate;
      return v != null ? [s.date, v] : [s.date, null];
    }),
  }));

  const target = BUCKET_TARGETS[historyBucket]?.mid ?? 50;

  return {
    backgroundColor: "transparent",
    grid: { left: 50, right: 16, top: 30, bottom: 24, containLabel: true },
    legend: {
      top: 4,
      textStyle: { color: "#a1a1a6", fontSize: 10 },
      itemWidth: 12, itemHeight: 8,
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: "rgba(20, 20, 22, 0.96)",
      borderColor: "rgba(255,255,255,0.10)",
      textStyle: { color: "#e6e6eb", fontSize: 12 },
    },
    xAxis: {
      type: "category",
      data: snaps.map((s) => s.date),
      axisLabel: { color: "#6e6e73", fontSize: 9 },
      axisLine:  { lineStyle: { color: "#2c2c2e" } },
    },
    yAxis: {
      type: "value",
      min: 0, max: 100,
      axisLabel: { color: "#a1a1a6", fontSize: 10, formatter: "{value}%" },
      splitLine: { lineStyle: { color: "#1c1c1e" } },
    },
    series: [
      ...series,
      {
        name: "target",
        type: "line",
        data: snaps.map((s) => [s.date, target]),
        showSymbol: false,
        lineStyle: { color: "#a5a3f0", type: "dashed", width: 1 },
        z: 0,
      },
    ],
    color: [
      "#9ca3af", // all — neutral grey
      "#ff9f0a", // NBA  — orange
      "#2dd4bf", // WNBA — teal
      "#5b8ddb", // MLB  — blue
      "#bf5af2", // NHL  — purple
      "#5fd47f", // Soccer — green
      "#ff6b6b", // UFC  — red
    ],
  } as echarts.EChartsOption;
}

// =============================================================
// Hero — health pill + totals + biggest miss callout
// =============================================================
function heroSection(cal: CalibrationSnapshot, miss: BiggestMiss | null): TemplateResult {
  const winRate = cal.n_decided > 0 ? (cal.wins / cal.n_decided * 100) : null;
  const healthCls = cal.health === "green" ? "is-ok"
                  : cal.health === "yellow" ? "is-warn"
                  : cal.health === "red" ? "is-bad" : "is-idle";
  const pnlCls = cal.pnl_units >= 0 ? "is-pos" : "is-neg";

  return html`
    <section class="panel cal-hero">
      <div class="cal-hero__top">
        <div class="cal-hero__pill-block">
          <span class="cal-hero__label">calibration health</span>
          <span class="cal-hero__pill cal-hero__pill--${healthCls}">
            ${cal.health.toUpperCase()}
          </span>
        </div>

        <div class="cal-hero__metric">
          <span class="cal-hero__label">window</span>
          <span class="cal-hero__value">${cal.window_days ?? "?"}d</span>
          <span class="cal-hero__sub">as of ${cal.as_of}</span>
        </div>
        <div class="cal-hero__metric">
          <span class="cal-hero__label">decided</span>
          <span class="cal-hero__value">${cal.n_decided}</span>
          <span class="cal-hero__sub">${cal.wins}W · ${cal.losses}L</span>
        </div>
        <div class="cal-hero__metric">
          <span class="cal-hero__label">win rate</span>
          <span class="cal-hero__value">
            ${winRate != null ? winRate.toFixed(1) + "%" : "—"}
          </span>
        </div>
        <div class="cal-hero__metric">
          <span class="cal-hero__label">P&amp;L</span>
          <span class="cal-hero__value cal-hero__value--${pnlCls}">
            ${cal.pnl_units >= 0 ? "+" : ""}${cal.pnl_units.toFixed(2)}u
          </span>
        </div>

        <span class="cal-hero__spacer"></span>

        ${renderExplainButton({
          label: "Explain calibration",
          context: () => buildOverallExplainContext(cal, miss),
        })}
      </div>

      ${miss ? html`
        <div class="cal-hero__miss">
          <span class="cal-hero__miss-tag">biggest miss</span>
          <span class="cal-hero__miss-body">
            <strong>${miss.sport}</strong> · bucket <code>${miss.bucket}</code>
            hits at <strong>${miss.hit_rate?.toFixed(1) ?? "—"}%</strong>
            vs target <strong>${miss.target_mid}%</strong>
            <span class="cal-hero__miss-delta is-bad">
              (${miss.delta_pp! >= 0 ? "+" : ""}${miss.delta_pp?.toFixed(1)}pp · n=${miss.decided})
            </span>
          </span>
        </div>
      ` : html`
        <div class="cal-hero__miss cal-hero__miss--ok">
          <span class="cal-hero__miss-tag is-ok">no miscalibration</span>
          <span class="cal-hero__miss-body">All active buckets within ±5pp of target.</span>
        </div>
      `}
    </section>
  `;
}

// =============================================================
// Bucket cards — large hit-rate number per bucket
// =============================================================
function bucketsSection(cal: CalibrationSnapshot): TemplateResult {
  const allBuckets = cal.heatmap.filter((c) => c.sport === "all");
  const cardsToShow = allBuckets.filter((c) => c.decided > 0);

  if (cardsToShow.length === 0) {
    return html`
      <section class="panel" style="padding:24px;text-align:center;">
        <span style="color:var(--text-tertiary);font-size:13px;">
          No decided bets in any bucket yet for this window.
        </span>
      </section>
    `;
  }

  return html`
    <section class="cal-section">
      <header class="cal-section__head">
        <h3>Per-bucket calibration · all sports</h3>
        <span class="cal-section__sub">
          Each bucket should hit at-or-near its target midpoint;
          delta is in percentage points
        </span>
      </header>
      <div class="cal-buckets">
        ${cardsToShow.map(bucketCard)}
      </div>
    </section>
  `;
}

function bucketCard(c: CalibrationCell): TemplateResult {
  const target = BUCKET_TARGETS[c.bucket];
  const hitRate = c.hit_rate ?? 0;
  const delta = c.delta_pp ?? 0;
  const cls = c.ok === false ? "is-bad"
            : Math.abs(delta) > 10 ? "is-warn"
            : Math.abs(delta) > 5  ? "is-meh"
            : "is-ok";
  const arrow = delta >= 0 ? "▲" : "▼";

  // Calc marker position on the bucket's range as a percentage (where
  // does the actual hit rate sit on the bucket's lo-hi axis)
  const rangeLo = target?.lo ?? 0;
  const rangeHi = target?.hi ?? 100;
  const targetPos = target ? ((target.mid - rangeLo) / (rangeHi - rangeLo) * 100) : 50;
  const actualPos = target ? Math.max(0, Math.min(100, ((hitRate - rangeLo) / (rangeHi - rangeLo) * 100))) : 50;

  return html`
    <article class="cal-bucket cal-bucket--${cls}">
      <header class="cal-bucket__head">
        <span class="cal-bucket__label">bucket</span>
        <span class="cal-bucket__name">${c.bucket}</span>
        <span class="cal-bucket__n">${c.decided}n</span>
      </header>

      <div class="cal-bucket__metric">
        <span class="cal-bucket__hit">${hitRate.toFixed(1)}<span>%</span></span>
        <span class="cal-bucket__target">target ${target?.mid ?? "—"}%</span>
      </div>

      <div class="cal-bucket__bar" title="actual ${hitRate.toFixed(1)}% vs target ${target?.mid}%">
        <span class="cal-bucket__bar-target" style="left:${targetPos}%;"></span>
        <span class="cal-bucket__bar-actual cal-bucket__bar-actual--${cls}"
              style="left:${actualPos}%;"></span>
        <span class="cal-bucket__bar-range">
          <span>${rangeLo}</span>
          <span>${rangeHi}</span>
        </span>
      </div>

      <footer class="cal-bucket__delta cal-bucket__delta--${cls}">
        ${arrow} ${delta >= 0 ? "+" : ""}${delta.toFixed(1)}pp
        <span class="cal-bucket__delta-note">
          ${cls === "is-ok"   ? "on target"
           : cls === "is-meh" ? "drift"
           : cls === "is-warn" ? "drifting"
           : "miscalibrated"}
        </span>
      </footer>
    </article>
  `;
}

// =============================================================
// Sport grid
// =============================================================
function sportsSection(cal: CalibrationSnapshot): TemplateResult {
  const sportsExceptAll = cal.sports.filter((s) =>
    s.id !== "all" && (s.n_decided > 0 || s.id === "NFL"));
  if (sportsExceptAll.length === 0) {
    return html``;
  }
  return html`
    <section class="cal-section">
      <header class="cal-section__head">
        <h3>Per-sport breakdown</h3>
        <span class="cal-section__sub">
          win rate · P&amp;L · per-bucket sample distribution
        </span>
      </header>
      <div class="cal-sports">
        ${sportsExceptAll.map((s) => sportTile(s, cal))}
      </div>
    </section>
  `;
}

function sportTile(sport: CalibrationSport, cal: CalibrationSnapshot): TemplateResult {
  const sportCells = cal.heatmap.filter((c) => c.sport === sport.id);
  const totalWins = sportCells.reduce((s, c) => s + c.hits, 0);
  const wr = sport.n_decided > 0 ? (totalWins / sport.n_decided) * 100 : null;
  const wrCls = wr == null ? "is-idle"
              : wr >= 60 ? "is-ok"
              : wr >= 50 ? "is-warn"
              : "is-bad";
  const pnlCls = sport.pnl_units > 0 ? "is-pos" : sport.pnl_units < 0 ? "is-neg" : "";
  const color = SPORT_COLORS[sport.id] ?? "var(--text-secondary)";

  return html`
    <article class="cal-sport-tile">
      <header class="cal-sport-tile__head">
        <span class="cal-sport-tile__name" style="color:${color};">${sport.id}</span>
        <span class="cal-sport-tile__n">${sport.n_decided} decided</span>
      </header>

      <div class="cal-sport-tile__metrics">
        <div>
          <span class="cal-sport-tile__metric-label">WR</span>
          <span class="cal-sport-tile__metric-value cal-sport-tile__metric-value--${wrCls}">
            ${wr != null ? wr.toFixed(1) + "%" : "—"}
          </span>
        </div>
        <div>
          <span class="cal-sport-tile__metric-label">P&amp;L</span>
          <span class="cal-sport-tile__metric-value ${pnlCls}">
            ${sport.pnl_units >= 0 ? "+" : ""}${sport.pnl_units.toFixed(2)}u
          </span>
        </div>
      </div>

      <div class="cal-sport-tile__buckets">
        ${sportCells.filter((c) => c.decided > 0).map((c) => sportBucketDot(c, color))}
        ${sportCells.every((c) => c.decided === 0) ? html`
          <span class="cal-sport-tile__no-buckets">no decided buckets</span>
        ` : ""}
      </div>
    </article>
  `;
}

function sportBucketDot(c: CalibrationCell, sportColor: string): TemplateResult {
  const delta = c.delta_pp ?? 0;
  const cls = Math.abs(delta) <= 5  ? "is-ok"
            : Math.abs(delta) <= 10 ? "is-meh"
            : Math.abs(delta) <= 20 ? "is-warn"
            : "is-bad";
  void sportColor;
  return html`
    <span class="cal-sport-bucket cal-sport-bucket--${cls}"
          title="${c.bucket}: ${c.hit_rate?.toFixed(1) ?? "—"}% (Δ${delta >= 0 ? "+" : ""}${delta.toFixed(1)}pp, n=${c.decided})">
      <span class="cal-sport-bucket__name">${c.bucket}</span>
      <span class="cal-sport-bucket__hit">${c.hit_rate?.toFixed(0) ?? "—"}%</span>
      <span class="cal-sport-bucket__n">${c.decided}n</span>
    </span>
  `;
}

// =============================================================
// Comparison chart — single ECharts horizontal bar
// =============================================================
function comparisonChartCard(): TemplateResult {
  return html`
    <section class="panel cal-chart-card">
      <header class="cal-chart-card__head">
        <div>
          <h3>Hit rate vs target · per-bucket × per-sport</h3>
          <span class="cal-chart-card__sub">
            actual hit rate (filled bar) vs target midpoint (notch)
          </span>
        </div>
      </header>
      <div id="cal-comparison" class="cal-chart-card__canvas"
           style="height:380px;"></div>
    </section>
  `;
}

function mountCharts(cal: CalibrationSnapshot, dataKey: string): void {
  const newEl = document.getElementById("cal-comparison");
  if (!newEl) return;

  const stale = charts.comparison.el && charts.comparison.el !== newEl;
  if (stale && charts.comparison.inst) {
    charts.comparison.inst.dispose();
    charts.comparison.inst = null;
    lastDataKey = "";
  }
  charts.comparison.el = newEl;

  if (!charts.comparison.inst) {
    charts.comparison.inst = echarts.init(newEl, "dark");
  }

  if (dataKey === lastDataKey) return;
  lastDataKey = dataKey;

  charts.comparison.inst.setOption(comparisonOption(cal), true);

  if (!(window as any).__calLabResize) {
    (window as any).__calLabResize = true;
    window.addEventListener("resize", () => charts.comparison.inst?.resize());
  }
}

function comparisonOption(cal: CalibrationSnapshot): echarts.EChartsOption {
  // One bar per (sport × bucket) pair, where the bar's value is the
  // ACTUAL hit rate. A scatter overlay marks the TARGET midpoint per
  // bucket so misalignment is visible at a glance.
  const cells = cal.heatmap.filter((c) => c.decided > 0 && c.hit_rate != null);
  // Sort: all first, then by sport order, then bucket order
  const bucketOrder = ["90+", "70-89", "50-69", "<50"];
  const sportOrder = ["all", "NBA", "WNBA", "MLB", "NHL", "NFL", "Soccer", "UFC", "Tennis"];
  cells.sort((a, b) => {
    const sa = sportOrder.indexOf(a.sport); const sb = sportOrder.indexOf(b.sport);
    if (sa !== sb) return sa - sb;
    return bucketOrder.indexOf(a.bucket) - bucketOrder.indexOf(b.bucket);
  });

  const cats = cells.map((c) => `${c.sport} · ${c.bucket}`);
  const actualData = cells.map((c) => {
    const target = BUCKET_TARGETS[c.bucket];
    const delta  = (c.delta_pp ?? 0);
    const cls    = c.ok === false ? "#ff6b6b"
                : Math.abs(delta) > 10 ? "#fbbf24"
                : Math.abs(delta) > 5  ? "#ffb43d"
                : "#5fd47f";
    void target;
    return { value: c.hit_rate, itemStyle: { color: cls } };
  });
  const targetData = cells.map((c) => {
    const target = BUCKET_TARGETS[c.bucket];
    return target ? target.mid : null;
  });

  return {
    backgroundColor: "transparent",
    grid: { left: 100, right: 50, top: 30, bottom: 40, containLabel: true },
    legend: {
      top: 6,
      textStyle: { color: "#a1a1a6", fontSize: 11 },
      data: ["actual hit %", "target midpoint"],
    },
    tooltip: {
      trigger: "item",
      backgroundColor: "rgba(20, 20, 22, 0.96)",
      borderColor: "rgba(255,255,255,0.10)",
      textStyle: { color: "#e6e6eb", fontSize: 12 },
      formatter: (p: any) => {
        const cell = cells[p.dataIndex];
        if (!cell) return "—";
        const t = BUCKET_TARGETS[cell.bucket];
        const delta = cell.delta_pp ?? 0;
        return `<b>${cell.sport} · ${cell.bucket}</b><br>` +
               `actual: ${cell.hit_rate?.toFixed(1)}% (${cell.hits}/${cell.decided})<br>` +
               `target: ${t?.mid}% (range ${t?.lo}–${t?.hi})<br>` +
               `delta:  ${delta >= 0 ? "+" : ""}${delta.toFixed(1)}pp`;
      },
    },
    xAxis: {
      type: "value",
      min: 0, max: 100, name: "hit %",
      nameTextStyle: { color: "#6e6e73", fontSize: 10 },
      axisLabel: { color: "#a1a1a6", fontSize: 10, formatter: "{value}%" },
      splitLine: { lineStyle: { color: "#1c1c1e" } },
    },
    yAxis: {
      type: "category",
      data: cats,
      axisLabel: { color: "#a1a1a6", fontSize: 11, fontWeight: "bold" as const },
      axisLine: { lineStyle: { color: "#2c2c2e" } },
    },
    series: [
      {
        name: "actual hit %",
        type: "bar",
        data: actualData,
        barWidth: "55%",
        label: {
          show: true,
          position: "right" as const,
          formatter: (p: any) => `${(p.value as number).toFixed(1)}%`,
          color: "#e6e6eb",
          fontSize: 10,
        },
      },
      {
        name: "target midpoint",
        type: "scatter",
        data: targetData.map((v, i) => v != null ? [v, i] : null).filter(Boolean) as any[],
        symbol: "diamond",
        symbolSize: 14,
        itemStyle: { color: "#a5a3f0", borderColor: "#fff", borderWidth: 1 },
      },
    ],
  } as echarts.EChartsOption;
}

// =============================================================
// Helpers
// =============================================================
interface BiggestMiss {
  sport:    string;
  bucket:   string;
  hit_rate: number | null;
  delta_pp: number | null;
  decided:  number;
  target_mid: number;
}

function findBiggestMiss(cal: CalibrationSnapshot): BiggestMiss | null {
  let worst: BiggestMiss | null = null;
  for (const c of cal.heatmap) {
    if (c.decided < 5) continue;        // ignore tiny samples
    if (c.delta_pp == null) continue;
    if (Math.abs(c.delta_pp) <= 5) continue;   // not a miss
    if (!worst || Math.abs(c.delta_pp) > Math.abs(worst.delta_pp ?? 0)) {
      const t = BUCKET_TARGETS[c.bucket];
      worst = {
        sport:    c.sport,
        bucket:   c.bucket,
        hit_rate: c.hit_rate,
        delta_pp: c.delta_pp,
        decided:  c.decided,
        target_mid: t?.mid ?? 50,
      };
    }
  }
  return worst;
}

function buildOverallExplainContext(cal: CalibrationSnapshot, miss: BiggestMiss | null): string {
  const lines = [
    `Calibration state as of ${cal.as_of} (${cal.window_days}d window):`,
    `${cal.n_decided} decided · ${cal.wins}W ${cal.losses}L · P&L ${cal.pnl_units}u · health=${cal.health}`,
    "",
  ];
  if (miss) {
    lines.push(
      `Biggest miss: ${miss.sport} ${miss.bucket} bucket — ` +
      `hit_rate=${miss.hit_rate?.toFixed(1)}%, target=${miss.target_mid}%, ` +
      `delta=${miss.delta_pp?.toFixed(1)}pp on n=${miss.decided}`,
    );
  } else {
    lines.push("No miscalibrated buckets — all within ±5pp of target.");
  }
  lines.push("", "Per bucket (all sports cohort):");
  for (const c of cal.heatmap.filter((cc) => cc.sport === "all" && cc.decided > 0)) {
    lines.push(`  ${c.bucket}: hr=${c.hit_rate?.toFixed(1)}%, Δ${c.delta_pp?.toFixed(1)}pp, n=${c.decided}`);
  }
  lines.push("", "Per sport:");
  for (const s of cal.sports.filter((sp) => sp.id !== "all" && (sp.n_decided > 0 || sp.id === "NFL"))) {
    lines.push(`  ${s.id}: ${s.n_decided}n, ${s.pnl_units >= 0 ? "+" : ""}${s.pnl_units}u, ${s.health}`);
  }
  return lines.join("\n");
}

// =============================================================
// State views
// =============================================================
function skeletonView(): TemplateResult {
  return html`
    <div class="cal-lab">
      <section class="panel" style="height:140px;"></section>
      <section class="panel" style="height:280px;"></section>
      <section class="panel" style="height:380px;"></section>
    </div>
  `;
}

function emptyView(): TemplateResult {
  return html`
    <div class="panel" style="padding:48px;text-align:center;">
      <div style="color:var(--text-tertiary);font-size:14px;line-height:1.5;
                   max-width:480px;margin:0 auto;">
        No calibration data yet. Source: dashboard.json's calibration block.
        Re-runs after each grader pass.
      </div>
    </div>
  `;
}

function errorView(err: Error): TemplateResult {
  return html`
    <div class="panel" style="padding:32px;border-color:rgba(255,69,58,0.32);">
      <div class="panel__title">
        <span>Calibration</span>
        <span class="phase-stub-badge"
              style="background:rgba(255,69,58,0.16);
                      color:var(--accent-red-light);
                      border-color:rgba(255,69,58,0.32);">
          fetch error
        </span>
      </div>
      <div style="color:var(--accent-red-light);font-size:12px;
                   line-height:1.5;font-family:monospace;
                   word-break:break-all;white-space:pre-wrap;
                   margin-top:8px;">
        ${err.message}
      </div>
    </div>
  `;
}
